import {LandPlot} from "../interfaces/card";
import {buffer, intersect, planarLength, touches} from "@arcgis/core/geometry/geometryEngine";
import Polygon from "@arcgis/core/geometry/Polygon";
import Geometry from "@arcgis/core/geometry/Geometry";

const BUFFER_DISTANCE = 1;

export const countSharedEdges = (initialPlot: LandPlot, adjacentPlots: LandPlot[]) => {
    if (!initialPlot.geometry) return adjacentPlots.map(plot => ({ ...plot, sharedEdges: 0 }));
    const initialBuffered = buffer(initialPlot.geometry, BUFFER_DISTANCE, "meters");

    return adjacentPlots.map(adjPlot => {
        if (!adjPlot.geometry) return { ...adjPlot, sharedEdges: 0 };
        const adjacentBuffered = buffer(adjPlot.geometry, BUFFER_DISTANCE, "meters") as Polygon;
        const testIntersection = intersect(initialBuffered, adjacentBuffered);

        if (!testIntersection || !(testIntersection as Polygon).rings) return { ...adjPlot, sharedEdges: 0 };

        let sharedEdgesCount = 0;
        const initialRings = (initialPlot.geometry as Polygon).rings[0];
        const adjacentRings = (adjPlot.geometry as Polygon).rings[0];

        for (let i = 0; i < initialRings.length - 1; i++) {
            const [x1, y1] = initialRings[i];
            const [x2, y2] = initialRings[i + 1];
            for (let j = 0; j < adjacentRings.length - 1; j++) {
                const [ax1, ay1] = adjacentRings[j];
                const [ax2, ay2] = adjacentRings[j + 1];
                if ((x1 === ax1 && y1 === ay1 && x2 === ax2 && y2 === ay2) ||
                    (x1 === ax2 && y1 === ay2 && x2 === ax1 && y2 === ay1)) {
                    sharedEdgesCount++;
                }
            }
        }
        return { ...adjPlot, sharedEdges: sharedEdgesCount };
    });
};

export const checkConnectivity = (updatedPlots: LandPlot[], initialPlot: LandPlot) => {
    if (!initialPlot) return [];
    const connectedPlots = [initialPlot];
    const queue = [initialPlot];

    while (queue.length) {
        const currentPlot = queue.shift();
        if (!currentPlot) continue;
        updatedPlots.forEach(plot => {
            const intersection = intersect(currentPlot.geometry, plot.geometry) as Geometry;
            const intersectionValid = intersection && planarLength(intersection, "meters") > 1;
            const plotsTouch = touches(currentPlot.geometry, plot.geometry);
            const sharedEdgesData = plotsTouch ? countSharedEdges(currentPlot, [plot])[0] : { sharedEdges: 0 };
            const hasSharedEdges = sharedEdgesData.sharedEdges > 0;
            if (!connectedPlots.some(p => p.fid === plot.fid) && (intersectionValid || hasSharedEdges)) {
                connectedPlots.push(plot);
                queue.push(plot);
            }
        });
    }
    return connectedPlots;
};